import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import {
  selectDataClinic,
  selectDataMatch,
  selectDataPatient,
  selectDataUserAuthenticated,
} from 'connect/selectors';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState } from 'pages/checkout/connect/types';
import { getInNetworkStatus } from 'pages/common/helpers/common-helpers';
import { useCallbackDeferred } from 'utils/customHooks/use-callback-deferred';
import { useCheckoutPatient } from 'utils/customHooks/use-checkout-patient';
import { useHookResult } from 'utils/customHooks/use-hook-result';
import { useIsPartnerUser } from 'utils/customHooks/use-partner-user';
import Logger from 'utils/Logger';

import { stepDefinitions } from '../steps/stepDefinitions';
import { CheckoutStepDefinition, CheckoutStepParams } from '../steps/types';

const getFirstApplicableStepDefinition = (params: CheckoutStepParams) => {
  const applicableStepDefinitions = getApplicableStepDefinitions(params);

  if (!applicableStepDefinitions.length) {
    Logger.error('No applicable step definitions found', { params });
    return null;
  }

  return applicableStepDefinitions[0]!;
};

const getApplicableStepDefinitions = (params: CheckoutStepParams) =>
  stepDefinitions.reduce<CheckoutStepDefinition[]>((acc, step) => {
    // A step is only applicable if the previous step is complete (or this is the first step)
    const isPreviousApplicableStepComplete = (() => {
      if (!acc.length) {
        return true;
      }
      // Previous step is always the last step in the accumulator
      const previousStep = acc.at(-1) as CheckoutStepDefinition;
      return previousStep.isComplete(params);
    })();

    const isStepApplicable = step.isApplicable(params);

    if (isPreviousApplicableStepComplete && isStepApplicable) {
      acc.push(step);
    }

    return acc;
  }, []);

const findFirstIncompleteStep = (params: CheckoutStepParams) => {
  const applicableStepDefinitions = getApplicableStepDefinitions(params);
  const firstIncompleteStep = applicableStepDefinitions
    .find((step) => !step.isComplete(params));

  if (!firstIncompleteStep) {
    return null;
  }

  return firstIncompleteStep;
};

type CurrentStepResult = {
  currentStep: CheckoutStepDefinition;
  isCurrentStepComplete: boolean;
  currentStepIndex: number;
  applicableStepDefinitions: CheckoutStepDefinition[];
};

const createCurrentStepResult = (
  step: CheckoutStepDefinition,
  params: CheckoutStepParams,
): CurrentStepResult => {
  const applicableStepDefinitions = getApplicableStepDefinitions(params);
  const currentStepIndex = applicableStepDefinitions.indexOf(step);

  const isCurrentStepComplete = step?.isComplete(params);

  return {
    currentStep: step,
    currentStepIndex,
    isCurrentStepComplete,
    applicableStepDefinitions,
  };
};

const getCurrentStep = (params: CheckoutStepParams): CurrentStepResult => {
  const defaultCheckoutStep = getFirstApplicableStepDefinition(params) || stepDefinitions[0]!;
  const defaultResult = createCurrentStepResult(defaultCheckoutStep, params);

  const { pathname } = params;
  if (!pathname.includes('checkout')) {
    return defaultResult;
  }

  // URL structure is /results/:clinicId/checkout/:step/:substep
  const currentStepPath = pathname.split('/').at(4);
  const currentStepFromPath = stepDefinitions.find((step) => step.path === currentStepPath);

  // If the current step is not found based on the URL, return the default result
  if (!currentStepFromPath) {
    // Only log an error if we have a step path
    if (currentStepPath) {
      Logger.error('Current step not found', { pathname });
    }
    return defaultResult;
  }

  return createCurrentStepResult(currentStepFromPath, params);
};

export const useIsEmailVerificationSkipped = () => {
  const patient = useSelector(selectDataPatient);
  return useMemo(
    () => Boolean(patient?.metadata?.isEmailVerificationLinkSent),
    [patient?.metadata?.isEmailVerificationLinkSent],
  );
};

const useCheckoutStepParams = (): CheckoutStepParams => {
  const { pathname } = useLocation();

  const isPartnerUser = useIsPartnerUser();
  const patient = useCheckoutPatient();

  const isUserAuthenticated = useSelector(selectDataUserAuthenticated);
  const checkoutState = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const match = useSelector(selectDataMatch);
  const clinic = useSelector(selectDataClinic);

  const isEmailVerificationSkipped = useIsEmailVerificationSkipped();

  return useHookResult({
    pathname,
    checkoutState,
    patient,
    isUserAuthenticated,
    isPartnerUser,
    isEmailVerificationSkipped,
    match,
    clinic,
  });
};

// Shared source of truth for whether the patient's selected insurance plan is in-network
// with the *clinic* (as opposed to the specific provider). This must be used everywhere
// in-network status is calculated during checkout, so the badge shown to the patient and
// the value submitted with the appointment request always agree.
export const useIsInNetwork = () => {
  const { clinic, checkoutState } = useCheckoutStepParams();
  const insurancePlanId = checkoutState?.insurance?.plan?.id;

  return useMemo(() => {
    if (!insurancePlanId) {
      return false;
    }

    return getInNetworkStatus(insurancePlanId, clinic?.insurancePlans);
  }, [insurancePlanId, clinic?.insurancePlans]);
};

export const useCurrentCheckoutStep = () => {
  const params = useCheckoutStepParams();
  return useMemo(() => getCurrentStep(params), [params]);
};

const getNextStep = ({
  currentStep,
  currentStepIndex,
  applicableStepDefinitions,
  params,
}: {
  currentStep: CheckoutStepDefinition;
  currentStepIndex: number;
  applicableStepDefinitions: CheckoutStepDefinition[];
  params: CheckoutStepParams;
}) => {
  let nextStepDefinition = applicableStepDefinitions[currentStepIndex + 1];

  const firstIncompleteStep = findFirstIncompleteStep(params);

  // Check if the current step is in the list of applicable steps
  const isCurrentStepApplicable = applicableStepDefinitions.includes(currentStep);

  // Check if the current step is past the first incomplete step
  // E.g. the user hasn't completed login but the URL is for the confirm step
  const firstIncompleteStepIndex = firstIncompleteStep ? applicableStepDefinitions.indexOf(firstIncompleteStep) : -1;
  const isCurrentStepPastFirstIncompleteStep = firstIncompleteStep && firstIncompleteStepIndex < currentStepIndex;

  // If the current step is not applicable, or if it's past the first incomplete step,
  // then use the first incomplete step if it exists
  const shouldUseFirstIncompleteStep = Boolean(!isCurrentStepApplicable || isCurrentStepPastFirstIncompleteStep);
  if (firstIncompleteStep && shouldUseFirstIncompleteStep) {
    nextStepDefinition = firstIncompleteStep;
  }

  // When the user is not authenticated, there is only one applicable step, so there is no next step
  if (!nextStepDefinition) {
    return applicableStepDefinitions[0]!;
  }

  return nextStepDefinition;
};

export const useCheckoutBaseUrl = () => {
  const { clinicId } = useParams<{ clinicId: string; }>();
  return useMemo(() => `/results/${clinicId}/checkout`, [clinicId]);
};

export const useNextCheckoutStep = () => {
  const history = useHistory();

  const { currentStepIndex, applicableStepDefinitions, isCurrentStepComplete, currentStep } = useCurrentCheckoutStep();

  const params = useCheckoutStepParams();

  const nextStep = useMemo(
    () => getNextStep({ currentStep, currentStepIndex, applicableStepDefinitions, params }),
    [currentStep, currentStepIndex, applicableStepDefinitions, params],
  );

  const baseUrl = useCheckoutBaseUrl();
  const nextStepUrl = useMemo(
    () => `${baseUrl}/${nextStep.path}`,
    [nextStep.path, baseUrl],
  );

  const goToNextStep = useCallbackDeferred(({ replace = false }: { replace?: boolean; } = {}) => {
    // Check if current step was completed
    if (!isCurrentStepComplete) {
      Logger.error('Checkout goToNextStep - Current step is incomplete. Unexpected behavior might occur.', { currentStep: currentStep?.id });
    }

    // Check if next step is not the same as the current step
    if (currentStep?.path === nextStep.path) {
      Logger.error('Checkout goToNextStep - Next step is the same as the current step. Preventing infinite loop.', { currentStep: currentStep?.id, nextStep: nextStep?.id });
      return;
    }

    const historyFn = replace ? history.replace : history.push;
    historyFn(nextStepUrl);
  }, [
    history,
    nextStepUrl,
    currentStep?.id,
    nextStep?.id,
    currentStep?.path,
    nextStep?.path,
    isCurrentStepComplete,
  ]);
  return { goToNextStep, nextStepUrl };
};
